import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Play, Pause, Volume2, VolumeX } from "lucide-react";
import { VIDEO_BANNER } from "@/data/catalog";

export const VideoSection = () => {
  const [available, setAvailable] = useState(false);
  const [playing, setPlaying] = useState(true);
  const [muted, setMuted] = useState(true);
  const videoRef = useRef(null);

  useEffect(() => {
    fetch(VIDEO_BANNER.video, { method: "HEAD" })
      .then((r) => {
        const ct = r.headers.get("content-type") || "";
        if (r.ok && ct.startsWith("video")) setAvailable(true);
      })
      .catch(() => {});
  }, []);

  if (!available) return null;

  const togglePlay = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) {
      v.play().catch(() => {});
      setPlaying(true);
    } else {
      v.pause();
      setPlaying(false);
    }
  };

  const toggleMute = () => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = !v.muted;
    setMuted(v.muted);
  };

  return (
    <section data-testid="video-section" className="py-16 sm:py-24 bg-[#1A4331] relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6 }}
          className="text-center max-w-2xl mx-auto mb-10"
        >
          <p className="text-xs uppercase tracking-[0.25em] font-semibold text-[#D4AF37] mb-3">
            {VIDEO_BANNER.overline}
          </p>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-[#F9F6F0] mb-4">
            {VIDEO_BANNER.title}
          </h2>
          <p className="text-base text-[#F9F6F0]/70 leading-relaxed">{VIDEO_BANNER.subtitle}</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
          className="relative max-w-3xl mx-auto"
        >
          <div className="absolute -inset-3 rounded-3xl border border-[#D4AF37]/30 pointer-events-none" />
          <div className="relative rounded-2xl overflow-hidden shadow-2xl bg-black">
            <video
              ref={videoRef}
              data-testid="brand-video"
              src={VIDEO_BANNER.video}
              poster={VIDEO_BANNER.poster}
              autoPlay
              muted
              loop
              playsInline
              onClick={togglePlay}
              className="w-full max-h-[70vh] object-contain cursor-pointer"
            />
            <div className="absolute bottom-3 right-3 flex gap-2">
              <button
                data-testid="video-mute-toggle"
                onClick={toggleMute}
                className="w-10 h-10 rounded-full bg-black/50 backdrop-blur-md text-white flex items-center justify-center hover:bg-[#C05C46] transition-colors"
                aria-label={muted ? "Unmute video" : "Mute video"}
              >
                {muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
              </button>
              <button
                data-testid="video-play-toggle"
                onClick={togglePlay}
                className="w-10 h-10 rounded-full bg-black/50 backdrop-blur-md text-white flex items-center justify-center hover:bg-[#C05C46] transition-colors"
                aria-label={playing ? "Pause video" : "Play video"}
              >
                {playing ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
